<template>
    <div class="dispatch-start">
        <!-- Instruction set -->
        <!-- <div class="content-block dx-card responsive-paddings instructions">
            <h3>Build a Dispatch</h3>
            <p>
                Create a new dispatch order or load a previous one. A dispatch moves
                through four steps:
            </p>
            <ol>
                <li><strong>Parameters</strong> &mdash; choose product, operator, area and limits.</li>
                <li><strong>Sites</strong> &mdash; select the sites to include.</li>
                <li><strong>Review &amp; Save</strong> &mdash; confirm details, then create a PDF or email it.</li>
            </ol>
            <p class="muted">Start by creating a new dispatch or loading a recent one below.</p>
        </div> -->

        <!-- Create New / Load buttons -->
        <div class="action-row">
            <div class="action-col">
                <DxButton class="big-btn create" icon="add" text="Create New" type="default"
                    styling-mode="contained" width="100%" @click="onCreateNew" />
            </div>
            <div class="action-col">
                <DxButton class="big-btn load" icon="folder" text="Load" type="normal"
                    styling-mode="contained" width="100%" @click="toggleAll" />
            </div>
        </div>

        <div v-if="deleteError" class="delete-error">{{ deleteError }}</div>

        <!-- Recent saved dispatches -->
        <div v-if="!showAll" ref="recent" class="recent-section">
            <h5 class="section-title">Recent</h5>
            <div v-if="!recentDispatches.length" class="muted no-recent">
                No recent dispatches found.
            </div>
            <div v-else class="recent-list">
                <div v-for="d in recentDispatches" :key="d.dispatchId" class="recent-card dx-card"
                    @click="onLoad(d)">
                    <div class="recent-card-header">
                        <span class="recent-name">{{ d.name }}</span>
                        <span class="recent-count">{{ d.siteCount }} sites</span>
                        <!-- Delete is deliberately only on the full Load list, not here.
                             Restore this button to bring it back to the Recent cards:
                        <button type="button" class="delete-btn" :disabled="deletingId != null"
                            :title="`Delete ${d.name || 'this dispatch'}`"
                            @click.stop="onDelete(d)">&times;</button>
                        -->
                    </div>
                    <div class="recent-meta">
                        <span class="chip">
                            <span :title="fmtFull(d.parameters.product)">{{
                                fmtList(d.parameters.product, 'products') }}</span>
                        </span>
                        <span class="chip" :title="fmtFull(d.parameters.operator)">{{
                            fmtList(d.parameters.operator, 'operators') }}</span>
                        <span class="chip" :title="fmtFull(d.parameters.area)">{{
                            fmtList(d.parameters.area, 'areas') }}</span>
                    </div>
                    <div class="recent-footer muted">
                        <span>
                            <template v-if="d.parameters.maxDte != null">≤ {{ d.parameters.maxDte }}d DTE</template>
                        </span>
                        <span>{{ formatDate(d.dispatchDate) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Full list of dispatches to load -->
        <div v-else class="all-section">
            <h5 class="section-title">Load a Dispatch ({{ allDispatches.length }})</h5>
            <div v-if="!allDispatches.length" class="muted no-recent">
                No dispatches found.
            </div>
            <div v-else class="all-list">
                <div v-for="d in allDispatches" :key="d.dispatchId" class="compact-card dx-card"
                    @click="onLoad(d)">
                    <div class="compact-main">
                        <span class="compact-name">{{ d.name || '(unnamed)' }}</span>
                        <span class="compact-sub muted">
                            <span :title="fmtFull(d.parameters.product)">{{
                                fmtList(d.parameters.product, 'products') }}</span>
                            · {{ d.siteCount }} sites
                        </span>
                    </div>
                    <span class="compact-date muted">{{ formatDate(d.dispatchDate) }}</span>
                    <button type="button" class="delete-btn" :disabled="deletingId != null"
                        :title="`Delete ${d.name || 'this dispatch'}`"
                        @click.stop="onDelete(d)">&times;</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { DxButton } from 'devextreme-vue/button';
import { confirm } from 'devextreme/ui/dialog';

// Longest list rendered in full before it collapses to a count.
const LIST_LIMIT = 8;

export default {
    name: 'DispatchStart',
    components: {
        DxButton,
    },
    data() {
        return {
            showAll: false,
            deletingId: null,
            deleteError: null,
        };
    },
    computed: {
        recentDispatches() {
            return this.$store.getters['dispatch/getRecentDispatches'];
        },
        allDispatches() {
            return [...(this.$store.getters['dispatch/getSavedDispatches'] || [])]
                .sort((a, b) => (b.dispatchId || 0) - (a.dispatchId || 0));
        },
    },
    emits: ['create-new', 'load'],
    methods: {
        onCreateNew() {
            this.$emit('create-new');
        },
        onLoad(dispatch) {
            this.$emit('load', dispatch);
        },
        toggleAll() {
            this.showAll = !this.showAll;
        },
        // Delete lives here rather than on the Review step so a dispatch can be
        // removed without loading it first — and so there is no loaded-but-deleted
        // dispatch left in the builder afterwards. The store still resets the
        // working copy if the deleted one happened to be open.
        async onDelete(dispatch) {
            if (this.deletingId != null) return;

            const label = dispatch.name || '(unnamed)';
            const ok = await confirm(
                `<p>Delete <b>${this.escape(label)}</b>?</p>
                 <p>${dispatch.siteCount || 0} selected site(s) will be discarded with it.
                 This cannot be undone.</p>`,
                'Delete Dispatch'
            );
            if (!ok) return;

            this.deletingId = dispatch.dispatchId;
            this.deleteError = null;
            try {
                await this.$store.dispatch('dispatch/deleteDispatch', dispatch.dispatchId);
            } catch (error) {
                this.deleteError =
                    (error && error.message) || `Could not delete "${label}".`;
            } finally {
                this.deletingId = null;
            }
        },
        // The confirm dialog renders HTML, so a dispatch name is escaped before
        // being interpolated into it.
        escape(value) {
            return String(value == null ? '' : value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
        },
        // Up to LIST_LIMIT entries are named; past that the specifics stop being
        // readable in a card and only the count is useful. The full list stays
        // available as the element's tooltip.
        fmtList(value, noun) {
            const list = this.toList(value);
            if (!list.length) return 'All';
            if (list.length <= LIST_LIMIT) return list.join(', ');
            return `${list.length} ${noun}`;
        },
        fmtFull(value) {
            const list = this.toList(value);
            return list.length ? list.join(', ') : 'All';
        },
        // Tolerates the legacy single-string form of these parameters.
        toList(value) {
            if (Array.isArray(value)) return value;
            return value ? [value] : [];
        },
        formatDate(value) {
            if (!value) return '';
            const d = new Date(value);
            if (isNaN(d)) return value;
            return d.toLocaleDateString();
        },
    },
};
</script>

<style scoped>
.dispatch-start {
    max-width: 900px;
    margin: 0 auto;
}

.instructions h3 {
    margin-top: 0;
}

.instructions ol {
    margin: 10px 0;
    padding-left: 20px;
}

.instructions li {
    margin: 6px 0;
}

.muted {
    color: rgba(150, 150, 150, 0.9);
    font-size: 13px;
}

.action-row {
    display: flex;
    gap: 20px;
    margin: 24px 20px;
}

.action-col {
    flex: 1;
}

.big-btn {
    height: 90px;
    font-size: 18px;
}

.recent-section,
.all-section {
    margin: 24px 20px;
}

/* Full, thinner, scrollable list shown via the Load button. */
.all-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 55vh;
    overflow-y: auto;
    padding-right: 4px;
}

.compact-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 5px;
    cursor: pointer;
    transition: transform 0.06s ease, box-shadow 0.06s ease;
}

.compact-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.compact-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.compact-name {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.compact-sub {
    font-size: 11px;
    margin-top: 1px;
}

.compact-date {
    font-size: 11px;
    white-space: nowrap;
    flex-shrink: 0;
}

.section-title {
    margin: 0 0 12px 0;
    font-weight: 100;
}

.recent-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.recent-card {
    padding: 14px 16px;
    cursor: pointer;
    border-radius: 6px;
    transition: transform 0.08s ease, box-shadow 0.08s ease;
}

.recent-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.recent-card-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 8px;
}

.recent-name {
    flex: 1 1 auto;
    min-width: 0;
}

.recent-name {
    font-size: 16px;
    font-weight: 500;
}

.recent-count {
    font-size: 12px;
    color: rgb(255, 125, 0);
}

.recent-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 8px;
}

.chip {
    font-size: 12px;
    padding: 2px 10px;
    border-radius: 12px;
    background: rgba(255, 115, 0, 0.15);
    color: rgb(255, 140, 40);
}

.recent-footer {
    display: flex;
    justify-content: space-between;
}

.no-recent {
    padding: 12px 0;
}

/* Subtle until hovered — deleting is not the reason anyone opens this list. */
.delete-btn {
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    line-height: 1;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: rgba(255, 255, 255, 0.35);
    font-size: 17px;
    cursor: pointer;
    transition: background 0.1s ease, color 0.1s ease;
}

.delete-btn:hover:not(:disabled) {
    background: rgba(217, 83, 79, 0.18);
    color: #ff6b6b;
}

.delete-btn:disabled {
    opacity: 0.4;
    cursor: default;
}

.delete-error {
    margin-bottom: 14px;
    padding: 10px 14px;
    border-radius: 4px;
    border-left: 4px solid #d9534f;
    background: rgba(217, 83, 79, 0.12);
    color: #ff8a94;
    font-size: 13px;
}
</style>
